<template>
    <div class="date-time-picker">
        <div class="date-time-picker-content">
            <date-range-picker
                v-if="isRange"
                v-model="range"
                :min-date="minDate"
                :max-date="maxDate"
                :disabled="disabled"
            />

            <template v-else>
                <date-picker
                    v-model="date"
                    :min-date="minDate"
                    :max-date="maxDate"
                    :disabled="disabled"
                />
                <time-picker
                    v-model="time"
                    :options="timeOptions || defaultTimeOptions"
                    :suggested-time="suggestedTime"
                    :disabled="disabled"
                />
            </template>
        </div>
        <div class="date-time-picker-footer">
            <span class="date-time-picker-datetime">
                {{ summary }}
            </span>

            <div class="date-time-picker-buttons">
                <button
                    type="button"
                    :disabled="disabled"
                    class="btn is-small date-time-picker-button date-time-picker-cancel"
                    @click.prevent="$emit('cancel')"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    :disabled="disabled"
                    class="btn is-small date-time-picker-button date-time-picker-submit"
                    @click.prevent="emitValue"
                >
                    Apply
                </button>
            </div>
        </div>
    </div>
</template>
<script>
import moment from 'moment-timezone';
import DatePicker from '@/components/Controls/DatePicker';
import DateRangePicker from '@/components/Controls/DateRangePicker';
import TimePicker from '@/components/Controls/TimePicker';

const DB_DATE_FORMAT = 'YYYY-MM-DD';
const DB_TIME_FORMAT = 'HH:mm:ss';
const LONG_DATE_FORMAT = 'MMMM Do, YYYY';
const SHORT_DATE_FORMAT = 'MMMM Do';
const LONG_TIME_FORMAT = 'h:mm A z';
const SHORT_TIME_FORMAT = 'h:mm A';

export default {
    components: {
        DatePicker,
        DateRangePicker,
        TimePicker,
    },

    model: {
        event: 'change',
        prop: 'value',
    },

    props: {
        minDate: {
            type: Date,
            default: null,
        },
        maxDate: {
            type: Date,
            default: null,
        },
        timeOptions: {
            type: Object,
            default: null,
        },
        suggestedTime: {
            type: String,
            default: null,
        },
        mode: {
            type: String,
            default: null,
        },
        value: {
            type: [String, Object],
            default: null,
        },
        disabled: {
            default: false,
            type: Boolean,
        },
    },

    data: () => ({
        date: null,
        time: null,
        range: null,
        today: moment().startOf('day'),
    }),

    computed: {
        /**
         * Determined whether picker is in range mode or not.
         *
         * @returns {boolean} Picker is in range mode or not?
         */
        isRange() {
            return 'range' === this.mode;
        },

        /**
         * Gets current date and time in moment object.
         *
         * @returns {moment|null} Date time.
         */
        datetime() {
            return this.date && this.time
                ? moment(`${this.date} ${this.time}`)
                : null;
        },

        /**
         * Gets current UTC date and time in datebase format.
         *
         * @returns {string|null} UTC Date time.
         */
        databaseDatetime() {
            return this.datetime ? this.datetime.utc().format(`${DB_DATE_FORMAT} ${DB_TIME_FORMAT}`) : null;
        },

        /**
         * Returns selected date and/or time summary.
         *
         * @returns {string} Date and/or time summary.
         */
        summary() {
            if (this.isRange) {
                return this.formattedRange;
            }

            return this.formattedDateTime;
        },

        /**
         * Returns formatted range.
         *
         * @returns {string|null} Formatted range.
         */
        formattedRange() {
            const { start = null, end = null } = this.range || {};

            if (! start || ! end) {
                return null;
            }

            /**
             * Formats date for range display.
             *
             * @param   {string} date Date to format.
             * @returns {string}      Formatted date.
             */
            const format = (date) => moment(date).format(LONG_DATE_FORMAT);

            return `${format(start)} - ${format(end)}`;
        },

        /**
         * Returns formatted date time.
         *
         * @returns {string|null} Formatted date time.
         */
        formattedDateTime() {
            if (! this.datetime) {
                return null;
            }

            const format = this.datetime.year() === this.today.year()
                ? `${SHORT_DATE_FORMAT} [at] ${LONG_TIME_FORMAT}`
                : `${LONG_DATE_FORMAT} [at] ${LONG_TIME_FORMAT}`;

            return this.datetime.format(format);
        },

        /**
         * Returns list of default time options.
         *
         * @returns {object} List of default time options.
         */
        defaultTimeOptions() {
            const time = this.today.clone();

            return Object.fromEntries(
                Array.from(
                    { length: 48 },
                    () => [
                        time.add(30, 'm').format(DB_TIME_FORMAT),
                        time.format(SHORT_TIME_FORMAT),
                    ]
                )
            );
        },
    },

    /**
     * Parses initial value to date and time.
     */
    mounted() {
        if (this.value) {
            if (this.isRange) {
                this.range = this.value || { start: null, end: null };
            } else {
                // Given value is considered to be UTC, convert to user timezone.
                const datetime = moment.utc(this.value).tz(moment.tz.guess());

                this.date = datetime.format(DB_DATE_FORMAT);
                this.time = datetime.format(DB_TIME_FORMAT);
            }
        }
    },

    methods: {
        /**
         * Emits selected value.
         */
        emitValue() {
            this.$emit('change', this.isRange ? this.range : this.databaseDatetime);
        },
    },
};
</script>
